'use client';

import React, {FC} from 'react';
import Slider, {Settings} from 'react-slick';
import Image from 'next/image';

import {useWindowSize} from '@zoefin/hooks';

import styles from './style.module.scss';

// Co-located with consumer so the carousel CSS is only loaded by routes
// that mount this component, not every page in the portal.
import 'slick-carousel/slick/slick.css';

type Testimonial = {
  initial: string;
  name: string;
  paragraph: React.ReactNode;
};

const TESTIMONIALS: Testimonial[] = [
  {
    initial: 'G',
    name: 'Glenn Y.',
    paragraph: (
      <>
        “Highly recommend Zoe for access to highly ranked financial advisors -
        Excellent knowledge of financial advisor business and superior customer
        service. We are aligned with a firm that far exceeds our expectations”.
        <br /> <br />
      </>
    ),
  },
  {
    initial: 'M',
    name: 'Marcia G.',
    paragraph: (
      <>
        “The experience was great and they provided several good options. I
        would recommend Zoe and their advisors to anyone that wants an
        independent advisor not tied to selling you their products”.
        <br /> <br /> <br />
      </>
    ),
  },
  {
    initial: 'R',
    name: 'Rachel O.',
    paragraph: (
      <>
        “Zoe took all the work out of finding a financial planner. I&apos;m
        really happy with the planner I found through Zoe, and I would recommend
        the company to anyone looking for help finding a financial
        professional.”
        <br /> <br />
      </>
    ),
  },
];

const SLIDER_SETTINGS: Settings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
  arrows: false,
  autoplay: false,
  draggable: true,
  pauseOnHover: false,
  touchMove: true,
  swipe: true,
  responsive: [
    {
      breakpoint: 1130,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 800,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const TestimonialCard: FC<Testimonial> = ({initial, name, paragraph}) => (
  <div
    data-testid="each-card"
    className={styles['clients__cards-container_card']}>
    <div className={styles['clients__cards-container_card_upper-items']}>
      <figure>
        <Image
          src="/static/img/icons/quotation-mark.svg"
          alt="quotation mark"
          className="img-responsive"
          width={32}
          height={32}
        />
      </figure>
      <div
        className={
          styles['clients__cards-container_card_upper-items_circle-span']
        }>
        <span
          className={
            styles[
              'clients__cards-container_card_upper-items_circle-span_letter'
            ]
          }>
          {initial}
        </span>
      </div>
    </div>
    <p data-testid="each-card-paragraph">
      {paragraph}
      <span className={styles['clients__cards-container_card_name']}>
        - {name}
      </span>
    </p>
    <div className={styles['clients__cards-container_card_lower-items']}>
      <figure>
        <Image
          src="/static/img/icons/icon_star.svg"
          alt="Point Star"
          className="img-responsive"
          width={20}
          height={20}
        />
      </figure>
      <p className={styles['clients__cards-container_card_lower-items_points']}>
        5
      </p>
    </div>
  </div>
);

const Clients: FC = () => {
  const windowSize = useWindowSize();

  // slick-react ignores `responsive` breakpoints after first render, so we
  // pick the matching breakpoint ourselves whenever the window size changes.
  const matchedSettings = !windowSize
    ? SLIDER_SETTINGS
    : (SLIDER_SETTINGS.responsive.findLast(
        (item) => windowSize.width <= item.breakpoint,
      )?.settings as Settings) || SLIDER_SETTINGS;
  const settings: Settings = {
    ...SLIDER_SETTINGS,
    slidesToShow: matchedSettings.slidesToShow,
    slidesToScroll: matchedSettings.slidesToScroll,
  };

  return (
    <div className={styles['section-clients']}>
      <div className="default-container">
        <div className={styles.clients}>
          <h3>Client Reviews</h3>
          <div className={styles['clients__cards-container']}>
            <Slider {...settings}>
              {TESTIMONIALS.map((testimonial) => (
                <TestimonialCard key={testimonial.initial} {...testimonial} />
              ))}
            </Slider>
          </div>
          <p className={styles['clients__disclaimer']}>
            The preceding testimonial was provided by an actual user of the Zoe
            Financial advisor matching service. No compensation was provided for
            the testimonial. These testimonials and endorsements do not
            guarantee a level of success, nor do they guarantee that all users
            will have the same experience. Zoe Financial and Trust Pilot are not
            affiliated.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Clients;
